// Workflow execution. A workflow is an agent-spine, goal-directed
// analysis: we drive `runSubAgent` (the same headless multi-step tool
// loop the chat's sub-agents use) with a read-only toolset and a fixed
// goal, RECORD the full step timeline + deliverable as a run, and
// persist it so the user can replay it and compare results over time.
//
// No new agent engine — runSubAgent already loops over tools and
// terminates on `return_result`. This module is the orchestration:
// param/scope binding, toolset assembly, run-record lifecycle, and
// turning the run into a persisted, comparable artifact.
//
// Returned shape: the final run record (also persisted via putRun).

import { runSubAgent } from "./subAgent.js";
import { buildWorkflowToolset } from "./workflowTools.js";
import { buildWorkflowSystemPrompt, buildWorkflowTask } from "./workflowPrompt.js";
import { substituteParams } from "./kernelRunner.js";
import { putRun } from "./workflowStore.js";
import { getCurrentTenantIdSync } from "./userInfo.js";

// Minimal schema forcing a clean return_result termination for a
// render-deliverable workflow. The actual artifact is the last
// render_block; this just carries a one-line summary + ends the loop.
const SUMMARY_SCHEMA = {
    type: "object",
    description: "A one-line summary of what this run found.",
    properties: { summary: { type: "string" } },
};

export async function runWorkflow({ workflow, params = {}, scope = null, onState, signal } = {}) {
    if (!workflow || typeof workflow !== "object") {
        throw new Error("runWorkflow needs a workflow definition");
    }
    const tenantId = workflow.tenantId || getCurrentTenantIdSync() || "default";
    const runId = newRunId();
    const namespaceKey = `wf:${workflow.command || workflow.id}:${runId}`;
    const getNamespaceKey = () => namespaceKey;

    const resolvedParams = resolveParams(workflow, params);
    const deliverableKind = workflow.deliverable?.kind === "structured" ? "structured" : "render";

    // Run record — created "running", finalised at the end. Same id /
    // key, so the terminal write replaces the initial one.
    const run = {
        id: runId,
        workflowId: workflow.id,
        command: workflow.command,
        params: resolvedParams,
        scope: Array.isArray(scope) && scope.length ? scope : null,
        status: "running",
        startedAt: Date.now(),
        endedAt: null,
        timeline: [],
        deliverable: null,
        error: null,
        // Analytics queries that never referenced the run's scope (each is
        // also flagged inline in the persisted run log). 0 for unscoped runs.
        scopeWarnings: 0,
    };
    emit(onState, run);
    try { await putRun(tenantId, run); } catch { /* fail-soft */ }

    // Live progress: a lightweight running step list driven off the
    // agent's event stream. The AUTHORITATIVE timeline is rebuilt from
    // the final message history below (it carries args + result
    // details the events don't reliably surface).
    const liveSteps = [];
    const onEvent = (event) => {
        try {
            if (event?.type === "tool_execution_start") {
                liveSteps.push({ name: event.toolName || "tool", status: "running" });
                emit(onState, { ...run, steps: liveSteps.slice() });
            } else if (event?.type === "tool_execution_end") {
                const last = liveSteps[liveSteps.length - 1];
                if (last) last.status = event.isError ? "error" : "done";
                emit(onState, { ...run, steps: liveSteps.slice() });
            }
        } catch { /* progress mustn't break the run */ }
    };

    let tools;
    try {
        tools = await buildWorkflowToolset({
            workflow,
            tenantId,
            getNamespaceKey,
            scope: run.scope,
            onScopeWarning: () => { run.scopeWarnings += 1; },
        });
    } catch (err) {
        return finalize(tenantId, run, { status: "error", error: err, messages: [] }, deliverableKind, onState);
    }

    const promptText = typeof workflow.promptTemplate === "string"
        ? substituteParams(workflow.promptTemplate, resolvedParams)
        : "";
    const task = buildWorkflowTask({
        goal: workflow.goal,
        promptText,
        params: resolvedParams,
        scope: run.scope,
    });
    const systemPrompt = buildWorkflowSystemPrompt({ deliverableKind });
    const outputSchema = deliverableKind === "structured"
        ? (workflow.deliverable?.schema && typeof workflow.deliverable.schema === "object"
            ? workflow.deliverable.schema
            : { type: "object" })
        : SUMMARY_SCHEMA;

    let outcome;
    try {
        outcome = await runSubAgent({
            task,
            systemPrompt,
            tools,
            outputSchema,
            onEvent,
            signal,
            role: "primary",
        });
    } catch (err) {
        outcome = { status: "error", error: err, messages: [] };
    }

    return finalize(tenantId, run, outcome, deliverableKind, onState);
}

function finalize(tenantId, run, outcome, deliverableKind, onState) {
    // Persist the agent's RAW message history — the run log is rendered
    // with the same trace-detail TurnBlock the explore view uses, so we
    // keep the messages verbatim rather than a bespoke timeline shape.
    const messages = Array.isArray(outcome?.messages) ? outcome.messages : [];
    const { lastRender, stepCount } = extractRunResult(messages);
    const status = mapStatus(outcome?.status);

    let deliverable;
    if (deliverableKind === "structured") {
        deliverable = { kind: "structured", result: outcome?.result ?? null };
    } else {
        const summary = outcome?.result && typeof outcome.result === "object"
            ? (typeof outcome.result.summary === "string" ? outcome.result.summary : null)
            : (typeof outcome?.result === "string" ? outcome.result : null);
        deliverable = { kind: "render", render: lastRender, summary };
    }

    const finalRun = {
        ...run,
        status,
        endedAt: Date.now(),
        messages,
        stepCount,
        deliverable,
        error: status === "error" ? errMessage(outcome) : null,
    };
    emit(onState, finalRun);
    // Persist the terminal record; fail-soft. Return regardless so the
    // caller (and live UI) always gets the run even if IDB is down.
    return putRun(tenantId, finalRun)
        .then(() => finalRun)
        .catch(() => finalRun);
}

// Pull the two things the runner derives from the agent's messages:
//   • lastRender — the last render_block's (spec, data), the run's
//     deliverable render.
//   • stepCount  — number of real tool calls (excludes return_result),
//     shown in the runs list's Steps column.
// The full step-by-step view is rebuilt from the stored messages at
// render time via the trace-detail TurnBlock — see WorkflowsPage.
export function extractRunResult(messages) {
    // Map toolCallId → tool name so we attribute results correctly: a
    // render_block result is the deliverable, but a kernel tool also
    // emits kernelBlock details and must NOT be mistaken for it.
    const toolName = new Map();
    for (const m of messages) {
        if (m?.role === "assistant" && Array.isArray(m.content)) {
            for (const b of m.content) if (b?.type === "toolCall") toolName.set(b.id, b.name);
        }
    }
    let lastRender = null;
    let stepCount = 0;
    for (const m of messages) {
        if (m?.role === "toolResult" && m.toolCallId) {
            const details = m.details;
            if (toolName.get(m.toolCallId) === "render_block" && details?.kind === "kernelBlock") {
                lastRender = { spec: details.spec, data: details.data };
            }
        }
        if (m?.role !== "assistant" || !Array.isArray(m.content)) continue;
        for (const block of m.content) {
            if (block?.type === "toolCall" && block.name !== "return_result") stepCount += 1;
        }
    }
    return { lastRender, stepCount };
}

// ── helpers ──────────────────────────────────────────────────────

function emit(onState, run) {
    try { onState?.({ ...run }); } catch { /* observability mustn't break the run */ }
}

function mapStatus(status) {
    if (status === "done") return "done";
    if (status === "aborted") return "aborted";
    return "error"; // "error", "no_return_result", or anything unexpected
}

function errMessage(outcome) {
    if (outcome?.error?.message) return outcome.error.message;
    if (outcome?.reason === "no_return_result") return "agent finished without producing a deliverable";
    return "workflow run failed";
}

function resolveParams(workflow, overrides) {
    const out = {};
    const decls = Array.isArray(workflow.params) ? workflow.params : [];
    for (const d of decls) {
        if (!d || typeof d.name !== "string") continue;
        const has = overrides && Object.prototype.hasOwnProperty.call(overrides, d.name);
        out[d.name] = coerce(has ? overrides[d.name] : d.default, d.type);
    }
    // Allow undeclared overrides through for ad-hoc invocation.
    if (overrides && typeof overrides === "object") {
        for (const [k, v] of Object.entries(overrides)) if (!(k in out)) out[k] = v;
    }
    return out;
}

function coerce(value, type) {
    if (value == null) return value;
    if (type === "number") {
        const n = Number(value);
        return Number.isFinite(n) ? n : value;
    }
    if (type === "boolean") {
        if (typeof value === "boolean") return value;
        if (value === "true") return true;
        if (value === "false") return false;
        return Boolean(value);
    }
    if (type === "string") return String(value);
    return value;
}

function newRunId() {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return `run_${crypto.randomUUID()}`;
        }
    } catch { /* fall through */ }
    return `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
